export const Feature = {
  Bank: 'bank',
  Qr: 'qr',
  Todos: 'todos',
  AutoInvoicing: 'autoInvoicing',
  ContractMaterial: 'contractMaterial',
  EmailInvoice: 'emailInvoice',
  Orders: 'orders',
  CashRegister: 'cashRegistry',
  CountryField: 'countryField',
  Webhook: 'webhook',
  SumUp: 'sumUp',
  InvoicePaidConfirmation: 'invoicePaidConfirmation',
  Catalog: 'catalog',
  Warehouse: 'warehouse',
  Eshop: 'eshop',
} as const

export type FeatureKey = (typeof Feature)[keyof typeof Feature]

export const ownerManageableFeatures = [
  Feature.CashRegister,
  Feature.Catalog,
  Feature.Warehouse,
  Feature.Eshop,
] as const

export type OwnerManageableFeature = (typeof ownerManageableFeatures)[number]

export function featureDependencies(feature: FeatureKey): readonly FeatureKey[] {
  switch (feature) {
    case Feature.Warehouse:
      return [Feature.Catalog]
    case Feature.Eshop:
      return [Feature.Catalog]
    default:
      return []
  }
}

export function expandFeaturesWithDependencies(
  features: readonly FeatureKey[],
): FeatureKey[] {
  const expanded: FeatureKey[] = [...features]

  for (const feature of features) {
    for (const dependency of featureDependencies(feature)) {
      if (!expanded.includes(dependency)) {
        expanded.push(dependency)
      }
    }
  }

  return expanded
}

export function toggleFeatureKeys(
  enabled: readonly FeatureKey[],
  feature: FeatureKey,
  checked: boolean,
): FeatureKey[] {
  if (!checked) {
    return enabled.filter((value) => value !== feature)
  }

  return expandFeaturesWithDependencies([...enabled, feature])
}

export function toggleFeatureIds(
  enabledIds: readonly number[],
  toggledId: number,
  checked: boolean,
  features: readonly { id: number; key: string }[],
): number[] {
  const idByKey = new Map(features.map((feature) => [feature.key, feature.id]))
  const keyById = new Map(features.map((feature) => [feature.id, feature.key as FeatureKey]))

  if (!checked) {
    return enabledIds.filter((id) => id !== toggledId)
  }

  const enabledKeys = enabledIds
    .map((id) => keyById.get(id))
    .filter((key): key is FeatureKey => key !== undefined)

  const toggledKey = keyById.get(toggledId)
  if (toggledKey === undefined) {
    return [...enabledIds]
  }

  return expandFeaturesWithDependencies([...enabledKeys, toggledKey])
    .map((key) => idByKey.get(key))
    .filter((id): id is number => id !== undefined)
}
